import * as React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import ImportLoansModal from '@/components/data-import/ImportLoansModal';

const HomePage: React.FC = () => {
  const [showImportModal, setShowImportModal] = React.useState(false);

  const handleImportClick = () => {
    setShowImportModal(true);
  };

  const handleCloseModal = () => {
    setShowImportModal(false);
  };

  const handleImportSuccess = () => {
    // Optionally refresh data or show a success message
  };

  return (
    <>
      <div
        className="page-container hero-section"
        data-test-id="home-page-container"
      >
        <div className="page-inner" data-test-id="home-page-inner">
          <div className="text-center mb-16" data-test-id="hero-section">
            <h1 className="hero-title" data-test-id="hero-title">
              RPX Loan Pricing Platform
            </h1>
            <p className="hero-subtitle" data-test-id="hero-subtitle">
              Comprehensive loan pricing and analysis platform for real estate
              investment professionals. Input loan parameters, run pricing
              scenarios, and analyze results with advanced risk metrics.
            </p>
          </div>
          <div className="grid-3" data-test-id="main-cards-grid">
            {/* Loan Input */}
            <Card className="card" data-test-id="loan-input-card">
              <div
                className="card-header"
                data-test-id="loan-input-card-header"
              >
                <div
                  className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6"
                  data-test-id="loan-input-icon-wrapper"
                >
                  <svg
                    className="w-8 h-8 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="loan-input-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      data-test-id="loan-input-icon-path"
                    />
                  </svg>
                </div>
              </div>
              <div className="card-body" data-test-id="loan-input-card-body">
                <h3 className="card-title" data-test-id="loan-input-card-title">
                  Loan Input
                </h3>
                <p
                  className="card-text"
                  data-test-id="loan-input-card-description"
                >
                  Enter comprehensive loan parameters including property
                  details, financial terms, and risk metrics.
                </p>
                <Link
                  to="/loan-form"
                  className="btn-primary"
                  data-test-id="loan-input-link"
                >
                  Start Input
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="loan-input-link-arrow"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                      data-test-id="loan-input-link-arrow-path"
                    />
                  </svg>
                </Link>
              </div>
            </Card>

            {/* Pricing Dashboard */}
            <Card className="card" data-test-id="pricing-dashboard-card">
              <div
                className="card-header"
                data-test-id="pricing-dashboard-card-header"
              >
                <div
                  className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                  data-test-id="pricing-dashboard-icon-wrapper"
                >
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="pricing-dashboard-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      data-test-id="pricing-dashboard-icon-path"
                    />
                  </svg>
                </div>
              </div>
              <div
                className="card-body"
                data-test-id="pricing-dashboard-card-body"
              >
                <h3
                  className="card-title"
                  data-test-id="pricing-dashboard-card-title"
                >
                  Pricing Dashboard
                </h3>
                <p
                  className="card-text"
                  data-test-id="pricing-dashboard-card-description"
                >
                  View portfolio overview, key metrics, sector allocation, and
                  risk distribution analysis.
                </p>
                <Link
                  to="/pricing-dashboard"
                  className="btn-primary"
                  data-test-id="pricing-dashboard-link"
                >
                  View Dashboard
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="pricing-dashboard-link-arrow"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                      data-test-id="pricing-dashboard-link-arrow-path"
                    />
                  </svg>
                </Link>
              </div>
            </Card>

            {/* Loan Results */}
            <Card className="card" data-test-id="loan-results-card">
              <div
                className="card-header"
                data-test-id="loan-results-card-header"
              >
                <div
                  className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6"
                  data-test-id="loan-results-icon-wrapper"
                >
                  <svg
                    className="w-8 h-8 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="loan-results-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      data-test-id="loan-results-icon-path"
                    />
                  </svg>
                </div>
              </div>
              <div className="card-body" data-test-id="loan-results-card-body">
                <h3
                  className="card-title"
                  data-test-id="loan-results-card-title"
                >
                  Loan Results
                </h3>
                <p
                  className="card-text"
                  data-test-id="loan-results-card-description"
                >
                  Detailed analysis of individual loans with pricing results,
                  risk metrics, and valuation data.
                </p>
              </div>
            </Card>

            {/* Scenario Comparison */}
            <Card className="card" data-test-id="scenario-comparison-card">
              <div
                className="card-header"
                data-test-id="scenario-comparison-card-header"
              >
                <div
                  className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6"
                  data-test-id="scenario-comparison-icon-container"
                >
                  <svg
                    className="w-8 h-8 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="scenario-comparison-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
              <div
                className="card-body"
                data-test-id="scenario-comparison-card-body"
              >
                <h3
                  className="card-title"
                  data-test-id="scenario-comparison-title"
                >
                  Scenario Comparison
                </h3>
                <p
                  className="card-text"
                  data-test-id="scenario-comparison-description"
                >
                  Compare different pricing scenarios including base case,
                  stress testing, and default scenarios.
                </p>
                <Link
                  to="/pricing-comparison"
                  className="btn-primary"
                  data-test-id="scenario-comparison-link"
                >
                  Compare Scenarios
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="scenario-comparison-arrow-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </Card>

            {/* Risk Analysis */}
            <Card className="card" data-test-id="risk-analysis-card">
              <div
                className="card-header"
                data-test-id="risk-analysis-card-header"
              >
                <div
                  className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6"
                  data-test-id="risk-analysis-icon-container"
                >
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="risk-analysis-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
              </div>
              <div className="card-body" data-test-id="risk-analysis-card-body">
                <h3 className="card-title" data-test-id="risk-analysis-title">
                  Risk Analysis
                </h3>
                <p
                  className="card-text"
                  data-test-id="risk-analysis-description"
                >
                  Advanced risk metrics including PD, LGD, credit spreads, and
                  portfolio risk distribution.
                </p>
                <Link
                  to="/pricing-dashboard"
                  className="btn-primary"
                  data-test-id="risk-analysis-link"
                >
                  Analyze Risk
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="risk-analysis-arrow-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </Card>

            {/* Portfolio Management */}
            <Card className="card" data-test-id="portfolio-management-card">
              <div
                className="card-header"
                data-test-id="portfolio-management-card-header"
              >
                <div
                  className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6"
                  data-test-id="portfolio-management-icon-container"
                >
                  <svg
                    className="w-8 h-8 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="portfolio-management-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                </div>
              </div>
              <div
                className="card-body"
                data-test-id="portfolio-management-card-body"
              >
                <h3
                  className="card-title"
                  data-test-id="portfolio-management-title"
                >
                  Portfolio Management
                </h3>
                <p
                  className="card-text"
                  data-test-id="portfolio-management-description"
                >
                  Comprehensive portfolio analysis with sector allocation,
                  duration management, and yield optimization.
                </p>
                <Link
                  to="/pricing-dashboard"
                  className="btn-primary"
                  data-test-id="portfolio-management-link"
                >
                  Manage Portfolio
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="portfolio-management-arrow-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </Card>

            {/* Import Loans */}
            <Card className="card" data-test-id="import-loans-card">
              <div
                className="card-header"
                data-test-id="import-loans-card-header"
              >
                <div
                  className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6"
                  data-test-id="import-loans-icon-wrapper"
                >
                  <svg
                    className="w-8 h-8 text-teal-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="import-loans-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
              </div>
              <div className="card-body" data-test-id="import-loans-card-body">
                <h3
                  className="card-title"
                  data-test-id="import-loans-card-title"
                >
                  Import Loans
                </h3>
                <p
                  className="card-text"
                  data-test-id="import-loans-card-description"
                >
                  Bulk import loans from Excel or CSV files to quickly build
                  your portfolio.
                </p>
                <button
                  onClick={handleImportClick}
                  className="btn-primary"
                  data-test-id="import-loans-button"
                >
                  Import Loans
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="import-loans-button-arrow"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </Card>
          </div>
          {/* Features Section */}
          <div className="mt-20" data-test-id="features-section">
            <h2
              className="section-title text-center mb-12"
              data-test-id="features-title"
            >
              Platform Features
            </h2>
            <div className="grid-4" data-test-id="features-grid">
              <div className="text-center" data-test-id="feature-item-0">
                <div
                  className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4"
                  data-test-id="real-time-pricing-icon-container"
                >
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="real-time-pricing-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3
                  className="font-semibold text-gray-900 mb-2"
                  data-test-id="real-time-pricing-title"
                >
                  Real-time Pricing
                </h3>
                <p
                  className="text-gray-600 text-sm"
                  data-test-id="real-time-pricing-description"
                >
                  Instant loan pricing with market-based yield curves and credit
                  spreads
                </p>
              </div>

              <div className="text-center" data-test-id="feature-item-1">
                <div
                  className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4"
                  data-test-id="risk-analytics-icon-container"
                >
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="risk-analytics-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <h3
                  className="font-semibold text-gray-900 mb-2"
                  data-test-id="risk-analytics-title"
                >
                  Risk Analytics
                </h3>
                <p
                  className="text-gray-600 text-sm"
                  data-test-id="risk-analytics-description"
                >
                  Advanced risk metrics including PD, LGD, and portfolio stress
                  testing
                </p>
              </div>

              <div className="text-center" data-test-id="feature-item-2">
                <div
                  className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4"
                  data-test-id="scenario-analysis-icon-container"
                >
                  <svg
                    className="w-6 h-6 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="scenario-analysis-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </div>
                <h3
                  className="font-semibold text-gray-900 mb-2"
                  data-test-id="scenario-analysis-title"
                >
                  Scenario Analysis
                </h3>
                <p
                  className="text-gray-600 text-sm"
                  data-test-id="scenario-analysis-description"
                >
                  Compare multiple pricing scenarios and stress test assumptions
                </p>
              </div>

              <div className="text-center" data-test-id="feature-item-3">
                <div
                  className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4"
                  data-test-id="reporting-icon-container"
                >
                  <svg
                    className="w-6 h-6 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    data-test-id="reporting-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      data-test-id="reporting-icon-path"
                    />
                  </svg>
                </div>
                <h3
                  className="font-semibold text-gray-900 mb-2"
                  data-test-id="reporting-title"
                >
                  Reporting
                </h3>
                <p
                  className="text-gray-600 text-sm"
                  data-test-id="reporting-description"
                >
                  Comprehensive reporting with exportable data and
                  visualizations
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ImportLoansModal
        isOpen={showImportModal}
        onClose={handleCloseModal}
        onSuccess={handleImportSuccess}
      />
    </>
  );
};

export default HomePage;
